import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  FilterOutlined,
  PlusOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Button,
  Input,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs from 'dayjs';
import React, {useMemo, useState} from 'react';
import {FaCheck} from 'react-icons/fa';
import {MdCoPresent} from 'react-icons/md';
import {useSelector} from 'react-redux';

import {StatCard} from '../../../CommonComponents/Cards';
import {
  RQAddAttendance,
  RQUpdateAttendance,
  RqSupervisorLookupAll,
} from '../../../reactQuery/apis';

import {EditAttendance} from './EditAttandance';
import {MarkAttendance} from './MarkAttandance';
import {MonthlyAttendanceReport} from './MonthlyAttandanceReport';

const {Title, Text} = Typography;

const STATUS_CONFIG = {
  PRESENT:  {label: 'Present',  color: 'success',    icon: <CheckCircleOutlined />},
  ABSENT:   {label: 'Absent',   color: 'error',      icon: <CloseCircleOutlined />},
  LATE:     {label: 'Late',     color: 'warning',    icon: <ClockCircleOutlined />},
  HALF_DAY: {label: 'Half Day', color: 'processing', icon: <ClockCircleOutlined />},
};

const OVERTIME_LABELS = {
  NONE:        'None',
  FIRST_HALF:  'First Half',
  SECOND_HALF: 'Second Half',
  FULL_DAY:   'Full day',
};

const TODAY = dayjs().format('YYYY-MM-DD');

const AttendanceManagement = () => {
  const token = useSelector(state => state.auth.tokenData);

  const [searchText, setSearchText]         = useState('');
  const [markEmployee, setMarkEmployee]     = useState(null);
  const [editRecord, setEditRecord]         = useState(null);
  const [reportEmployee, setReportEmployee] = useState(null);
  const [bulkMarkOpen, setBulkMarkOpen]     = useState(false);

  const {data: employeeList = [], isLoading: employeesLoading} =
    RqSupervisorLookupAll(token);

  const {mutate: addAttendance, isLoading: adding} = RQAddAttendance({
    onSuccess: () => {
      message.success('Attendance marked successfully');
      setMarkEmployee(null);
      setBulkMarkOpen(false);
    },
    onError: () => message.error('Failed to mark attendance'),
  });

  const {mutate: updateAttendance, isLoading: updating} = RQUpdateAttendance({
    onSuccess: () => {
      message.success('Attendance updated successfully');
      setEditRecord(null);
    },
    onError: () => message.error('Failed to update attendance'),
  });

  const todayAttendanceMap = useMemo(() => {
    const map = {};
    employeeList.forEach(emp => {
      const todayRecord = (emp.attendanceInfo ?? []).find(r => r.date === TODAY);
      if (todayRecord) {map[emp.id] = todayRecord;}
    });
    return map;
  }, [employeeList]);

  const stats = useMemo(() => {
    const total    = employeeList.length;
    const present  = Object.values(todayAttendanceMap).filter(r => r.status === 'PRESENT').length;
    const absent   = Object.values(todayAttendanceMap).filter(r => r.status === 'ABSENT').length;
    const other    = Object.values(todayAttendanceMap).filter(
      r => r.status === 'LATE' || r.status === 'HALF_DAY',
    ).length;
    const unmarked = total - Object.keys(todayAttendanceMap).length;
    return {total, present, absent, other, unmarked};
  }, [employeeList, todayAttendanceMap]);

  const tableData = useMemo(() => {
    if (!searchText.trim()) {return employeeList;}
    const q = searchText.toLowerCase();
    return employeeList.filter(emp =>
      `${emp.firstName} ${emp.lastName}`.toLowerCase().includes(q) ||
      emp.email?.toLowerCase().includes(q),
    );
  }, [employeeList, searchText]);

  const handleMarkSubmit = payload => {
    addAttendance({attendanceData: [payload], token});
  };

  const handleEditSubmit = payload => {
    updateAttendance({id: payload.id, attendanceData: payload, token});
  };

  const columns = [
    {
      title: 'Employee',
      key: 'employee',
      render: (_, emp) => (
        <div className={'flex items-center gap-3'}>
          <div className={'w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center shrink-0'}>
            <span className={'text-primary-600 font-semibold text-xs'}>
              {emp.firstName?.[0]}{emp.lastName?.[0]}
            </span>
          </div>
          <div>
            <Text className={'font-medium text-sm text-gray-800 block'}>
              {emp.firstName} {emp.lastName}
            </Text>
            <Text className={'text-xs text-gray-400'}>{emp.email}</Text>
          </div>
        </div>
      ),
    },
    {
      title: 'Today\'s Status',
      key: 'todayStatus',
      render: (_, emp) => {
        const record = todayAttendanceMap[emp.id];
        if (!record) {
          return <Tag color={'default'}>Not Marked</Tag>;
        }
        const cfg = STATUS_CONFIG[record.status] || {label: record.status, color: 'default'};
        return (
          <Tag color={cfg.color} icon={cfg.icon} className={'font-medium'}>
            {cfg.label}
          </Tag>
        );
      },
    },
    {
      title: 'Today\'s Overtime',
      key: 'overtimeType',
      render: (_, emp) => {
        const record = todayAttendanceMap[emp.id];
        if (!record || !record.overtimeType || record.overtimeType === 'NONE') {
          return <Text className={'text-gray-300'}>None</Text>;
        }
        return (
          <Tag color={'blue'} className={'font-medium'}>
            {OVERTIME_LABELS[record.overtimeType] ?? record.overtimeType}
          </Tag>
        );
      },
    },
    {
      title: 'Login Time',
      key: 'loginTime',
      render: (_, emp) => {
        const record = todayAttendanceMap[emp.id];
        if (!record?.loginTime) {return <Text className={'text-gray-300'}>—</Text>;}
        return (
          <Text className={'text-xs text-gray-500'}>
            {dayjs(record.loginTime).format('hh:mm A')}
          </Text>
        );
      },
    },
    {
      title: 'Total Records',
      key: 'totalRecords',
      render: (_, emp) => (
        <Text className={'text-sm text-gray-600'}>
          {(emp.attendanceInfo ?? []).length} days
        </Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'right',
      render: (_, emp) => {
        const todayRecord = todayAttendanceMap[emp.id];
        return (
          <Space onClick={e => e.stopPropagation()}>
            <Button
              disabled={!!todayRecord}
              onClick={() => setMarkEmployee(emp)}
              style={
                todayRecord
                  ? {
                    backgroundColor: '#00674F',
                    borderColor: '#22c55e',
                    color: '#ffffff',
                  }
                  : {}
              }
            >
              {todayRecord ? 'Marked' : 'Mark'}
            </Button>

            {todayRecord && (
              <Button
                type={'default'}
                size={'small'}
                icon={<EditOutlined />}
                onClick={() => setEditRecord(todayRecord)}
                className={'btn-secondary !py-4'}
              >
                Edit
              </Button>
            )}
          </Space>
        );
      },
    },
  ];

  return (
    <div className={'md:p-6 p-1 min-h-screen bg-gray-50'}>
      {/* Header */}
      <div className={'flex items-center justify-between mb-6 flex-wrap gap-3'}>
        <div className={'flex items-center gap-3'}>
          <div
            className={'w-12 h-10 rounded-xl flex items-center justify-center text-white'}
            style={{background: 'linear-gradient(135deg, #E83F25, #A62C2C)'}}
          >
            <MdCoPresent style={{fontSize: 22}} />
          </div>
          <Title level={3} className={'header !mb-0'}>Attendance Management</Title>
        </div>
      </div>

      <div className={'grid grid-cols-2 md:grid-cols-4 gap-3 mb-6'}>
        <StatCard label={'Total Employees'} value={stats.total}   icon={<UserOutlined />} />
        <StatCard label={'Present Today'}   value={stats.present} icon={<FaCheck />} />
        <StatCard label={'Absent Today'}    value={stats.absent}  icon={<CloseCircleOutlined />} />
        <StatCard label={'Late / Half Day'} value={stats.other}   icon={<ClockCircleOutlined />} />
      </div>

      <div className={'rounded-md border-0 shadow-sm'}>
        <div className={'flex items-center justify-between mb-3 flex-wrap gap-2'}>
          <div className={'flex flex-col gap-2'}>
            <div className={'flex gap-2'}>
              <FilterOutlined className={'text-indigo-500'} />
              <span className={'font-semibold text-gray-700 text-sm'}>Filters</span>
            </div>
            <Input
              prefix={<SearchOutlined className={'text-slate-400'} />}
              placeholder={'Search employee by name or email…'}
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              className={'flex justify-start max-w-xs rounded-lg'}
              allowClear
            />
          </div>

          <Button
            className={'btn-primary'}
            icon={<PlusOutlined />}
            onClick={() => setBulkMarkOpen(true)}
          >
            Mark Attendance
          </Button>
        </div>

        <Table
          tableLayout={'fixed'}
          size={'middle'}
          scroll={{x: 'max-content'}}
          dataSource={tableData}
          columns={columns}
          rowKey={'id'}
          loading={employeesLoading}
          pagination={{pageSize: 10, showSizeChanger: false}}
          locale={{emptyText: 'No employees found'}}
          onRow={emp => ({
            onClick: () => setReportEmployee(emp),
            className: 'cursor-pointer hover:bg-gray-50 transition-colors',
          })}
        />
      </div>

      <MarkAttendance
        open={!!markEmployee}
        onClose={() => setMarkEmployee(null)}
        employee={markEmployee}
        employeeList={employeeList}
        onSubmit={handleMarkSubmit}
        loading={adding}
        alreadyMarked={markEmployee ? !!todayAttendanceMap[markEmployee.id] : false}
      />

      <MarkAttendance
        open={bulkMarkOpen}
        onClose={() => setBulkMarkOpen(false)}
        employee={null}
        employeeList={employeeList}
        onSubmit={handleMarkSubmit}
        loading={adding}
        alreadyMarked={false}
      />

      <EditAttendance
        open={!!editRecord}
        onClose={() => setEditRecord(null)}
        record={editRecord}
        onSubmit={handleEditSubmit}
        loading={updating}
      />

      <MonthlyAttendanceReport
        open={!!reportEmployee}
        onClose={() => setReportEmployee(null)}
        employee={reportEmployee}
      />
    </div>
  );
};

export default AttendanceManagement;
